import React, { ReactNode } from 'react';
import { View, StyleSheet, ViewStyle, Platform } from 'react-native';
import { isWeb } from '../../utils/platformUtils';
import { WEB_CONTENT_MAX_WIDTH, WEB_CONTENT_PADDING_H } from '../../theme/webLayout';
import WebPageHeader from './WebPageHeader';

type Props = {
  children: ReactNode;
  style?: ViewStyle;
  /** When false, only applies horizontal constraint (for nested layouts). */
  fullHeight?: boolean;
  /** When false, outer panel does not scroll (use for screens with FlatList). Default true. */
  outerScroll?: boolean;
  /** Pinned footer outside the scroll region (web desktop). Scrollbar stays at the panel edge. */
  footer?: ReactNode;
  /** Desktop web title bar above content. Omit on Dashboard. */
  title?: string;
  subtitle?: string;
  onBack?: () => void;
  /** Optional action aligned to the top-right of the header card (web only). */
  headerAction?: ReactNode;
};

const webScrollClassName = Platform.OS === 'web' ? 'web-page-scroll' : undefined;

/**
 * Constrains content width on desktop web.
 * Scrolls at full main-panel width so the browser scrollbar sits at the viewport edge.
 */
const WebPage = ({
  children,
  style,
  fullHeight = true,
  outerScroll = true,
  footer,
  title,
  subtitle,
  onBack,
  headerAction,
}: Props) => {
  if (!isWeb) {
    return <>{children}</>;
  }

  const header = title ? (
    <View style={styles.headerInner}>
      <WebPageHeader
        title={title}
        subtitle={subtitle}
        onBack={onBack}
        headerAction={headerAction}
      />
    </View>
  ) : null;

  if (footer) {
    return (
      <View
        style={[
          styles.scrollHost,
          fullHeight && styles.scrollHostFlex,
          styles.scrollHostColumn,
          style,
        ]}
      >
        {header}
        <View
          className={webScrollClassName}
          style={[styles.scrollHost, styles.scrollHostFlex, styles.scrollHostScroll]}
        >
          <View style={styles.inner}>{children}</View>
        </View>
        <View style={styles.footer}>{footer}</View>
      </View>
    );
  }

  if (title || !outerScroll) {
    return (
      <View
        style={[
          styles.scrollHost,
          fullHeight && styles.scrollHostFlex,
          styles.scrollHostColumn,
          style,
        ]}
      >
        {header}
        {outerScroll ? (
          <View
            className={webScrollClassName}
            style={[styles.scrollHost, styles.scrollHostFlex, styles.scrollHostScroll]}
          >
            <View style={styles.inner}>{children}</View>
          </View>
        ) : (
          <View style={[styles.scrollHost, styles.scrollHostFlex, styles.scrollHostClip]}>
            <View style={styles.innerFlex}>{children}</View>
          </View>
        )}
      </View>
    );
  }

  return (
    <View
      className={webScrollClassName}
      style={[
        styles.scrollHost,
        fullHeight && styles.scrollHostFlex,
        styles.scrollHostScroll,
        style,
      ]}
    >
      <View style={styles.inner}>{children}</View>
    </View>
  );
};

const styles = StyleSheet.create({
  scrollHost: {
    width: '100%',
  },
  scrollHostScroll: {
    overflowY: 'auto',
    ...(Platform.OS === 'web'
      ? {
          height: '100%',
          scrollbarGutter: 'stable',
        }
      : {}),
  },
  scrollHostFlex: {
    flex: 1,
    minHeight: 0,
  },
  scrollHostColumn: {
    flexDirection: 'column',
    overflow: 'hidden',
  },
  scrollHostClip: {
    overflow: 'hidden',
  },
  headerInner: {
    width: '100%',
    maxWidth: WEB_CONTENT_MAX_WIDTH,
    paddingHorizontal: WEB_CONTENT_PADDING_H,
    paddingTop: 24,
    paddingBottom: 12,
    alignSelf: 'center',
    flexShrink: 0,
  },
  inner: {
    width: '100%',
    maxWidth: WEB_CONTENT_MAX_WIDTH,
    paddingHorizontal: WEB_CONTENT_PADDING_H,
    paddingBottom: 32,
    alignSelf: 'center',
  },
  innerFlex: {
    width: '100%',
    maxWidth: WEB_CONTENT_MAX_WIDTH,
    paddingHorizontal: WEB_CONTENT_PADDING_H,
    paddingBottom: 32,
    alignSelf: 'center',
    flex: 1,
    minHeight: 0,
  },
  footer: {
    width: '100%',
    flexShrink: 0,
  },
});

export default WebPage;
